<template>
    <main class="main-content">
        <div class="content-card">
            <div class="referral-banner">
                <div class="banner-left">
                    <div class="banner-info">
                        <div class="banner-credits">Earn 10,000 bonus credits</div>
                        <div class="banner-title">Refer &amp; Earn</div>
                    </div>
                </div>
                <div class="banner-right">
                    <div class="banner-how-title">HOW IT WORKS</div>
                    <ul class="banner-how-list">
                        <li>
                            <img src="@/assets/figs/referral/referral_share.png" alt="" class="how-list-icon" />
                            Share your invite link
                        </li>
                        <li>
                            <img src="@/assets/figs/referral/referral_star.png" alt="" class="how-list-icon" />
                            You will also receive invitation incentives after successful registration
                        </li>
                    </ul>
                </div>
                <div class="banner-picture">
                    <img src="@/assets/figs/referral/referral_coin.png" alt="Referral Coin"
                        style="width: 64px; height: 64px;" />
                </div>
            </div>
            <p>
                Share your unique link with friends and colleagues. For every new user that signs up through your
                link, you'll both receive <strong>10,000 bonus credits!</strong>
            </p>
            <div class="referral-link-box">
                <input type="text" :value="referralLink" readonly id="referralLink"
                    :placeholder="referralLinkPlaceholder" />
                <button class="btn btn-link" @click="copyLink" :disabled="!canCopyReferralLink">
                    {{ copyButtonText }}
                </button>
                <button class="btn btn-qr" @click="showQr" :disabled="!hasQr">QR code</button>
            </div>
            <div v-if="referralStatusMessage" class="referral-status">
                {{ referralStatusMessage }}
            </div>

            <div class="stats-grid">
                <div class="stat-card">
                    <div class="label">Total Referral Links</div>
                    <div class="value">{{ stats.totalReferralLinks }}</div>
                </div>
                <div class="stat-card">
                    <div class="label">Total Referrals</div>
                    <div class="value">{{ stats.totalReferrals }}</div>
                </div>
                <div class="stat-card">
                    <div class="label">Total Credits Earned</div>
                    <div class="value">{{ stats.totalCredits }}</div>
                </div>
            </div>
        </div>
        <div class="content-card">
            <h3>Referral History</h3>
            <div v-if="history.length === 0" class="no-history">There is no referral history, yet.</div>
            <table v-else class="history-table">
                <thead>
                    <tr>
                        <th>Date</th>
                        <th>Referred User</th>
                        <th>Type</th>
                        <th>Bonus Credits</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(entry, i) in history" :key="i">
                        <td>{{ formatDate(entry.date) }}</td>
                        <td>{{ entry.referred_user || entry.user_id || '' }}</td>
                        <td>{{ entry.type }}</td>
                        <td class="credits">+{{ entry.bonus_credits.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                        }) }}</td>
                    </tr>
                </tbody>
            </table>
        </div>
        <InfoPopup :showPopup="showQrPopup" title="Referral QR Code" :imageBase64="qrBase64" :showImage="true"
            :showDownload="true" :showCancel="true" :showConfirm="false" downloadFilename="referral_QR.png"
            @update:showPopup="val => showQrPopup = val">
            <p>Scan this QR code to visit your referral link.</p>
        </InfoPopup>
    </main>
</template>
<script>
import { getReferralSenderInfo, getReferralDashboard } from '@/api/referral';
import { onMounted, ref, computed, reactive } from 'vue';
import InfoPopup from '@/components/common/InfoPopupImage.vue';
export default {
    name: 'ReferralView',
    components: { InfoPopup },
    setup() {
        const copyButtonText = ref('Copy Link');
        const referralInfo = ref({
            eligible: false,
            referral: {
                link_id: '',
                program_id: -1,
                referral_url: '',
                unique_code: '',
                qr_code: '',
            }
        });
        const isLoadingReferral = ref(true);
        const referralError = ref('');
        const showQrPopup = ref(false);
        const qrBase64 = ref('');
        const fallbackQrError = ref('');
        const referralLink = computed(() => {
            const base = (process.env.VUE_APP_FRONTEND_URL || '').replace(/\/$/, '');
            const code = referralInfo.value?.referral?.unique_code || '';
            if (!base || !code) return '';
            return `${base}/register?ref=${encodeURIComponent(code)}`;
        });
        const backendReferralUrl = computed(() => referralInfo.value?.referral?.referral_url || '');
        const backendQrCode = computed(() => referralInfo.value?.referral?.qr_code || '');
        const canCopyReferralLink = computed(() => !!referralLink.value);
        const canUseBackendQr = computed(() =>
            !!backendQrCode.value && urlsMatch(backendReferralUrl.value, referralLink.value)
        );
        const hasQr = computed(() => !!referralLink.value && (canUseBackendQr.value || !fallbackQrError.value));
        const referralLinkPlaceholder = computed(() =>
            isLoadingReferral.value ? 'Loading referral link...' : 'Referral link is not available'
        );
        const referralStatusMessage = computed(() => {
            if (isLoadingReferral.value) return '';
            if (referralError.value) return referralError.value;
            if (!referralInfo.value?.eligible && referralInfo.value?.reason) return referralInfo.value.reason;
            if (!referralLink.value) return 'Your referral link is not available yet.';
            return '';
        });
        const stats = reactive({
            totalReferralLinks: 0,
            totalReferrals: 0,
            totalCredits: 0
        });
        const history = ref([]);

        function formatDate(dateStr) {
            if (!dateStr) return '';
            const d = new Date(dateStr);
            return d.toLocaleString();
        }

        function urlsMatch(first, second) {
            try {
                return new URL(first).href === new URL(second).href;
            } catch {
                return false;
            }
        }

        async function showQr() {
            const link = referralLink.value;
            if (!link) return;
            if (canUseBackendQr.value) {
                qrBase64.value = backendQrCode.value;
                showQrPopup.value = true;
                return;
            }
            try {
                const qrEndpoint = `https://api.qrserver.com/v1/create-qr-code/?size=512x512&data=${encodeURIComponent(link)}`;
                const resp = await fetch(qrEndpoint, { cache: 'no-store' });
                const blob = await resp.blob();
                const reader = new FileReader();
                const base64Promise = new Promise((resolve, reject) => {
                    reader.onloadend = () => resolve(reader.result);
                    reader.onerror = reject;
                });
                reader.readAsDataURL(blob);
                const dataUrl = await base64Promise;
                qrBase64.value = String(dataUrl);
                showQrPopup.value = true;
            } catch (e) {
                fallbackQrError.value = 'QR code is temporarily unavailable. Please use the copy link button.';
                console.error('Failed to generate QR PNG', e);
            }
        }
        function copyLink() {
            const value = referralLink.value;
            if (!value) return;
            if (navigator.clipboard) {
                navigator.clipboard.writeText(value)
                    .then(() => {
                        copyButtonText.value = 'Copied!';
                        setTimeout(() => (copyButtonText.value = 'Copy Link'), 2000);
                    })
                    .catch(err => console.error('Copy failed', err));
            } else {
                const textarea = document.createElement('textarea');
                textarea.value = value;
                document.body.appendChild(textarea);
                textarea.select();
                try {
                    document.execCommand('copy');
                    copyButtonText.value = 'Copied!';
                    setTimeout(() => (copyButtonText.value = 'Copy Link'), 2000);
                } catch (err) {
                    console.error('Fallback copy failed', err);
                }
                document.body.removeChild(textarea);
            }
        }
        async function loadReferralDashboard() {
            try {
                const dashboard_Data = await getReferralDashboard();
                const totals = dashboard_Data?.totals || {};
                stats.totalReferralLinks = totals.total_referral_links || 0;
                stats.totalReferrals = totals.total_referrals || 0;
                stats.totalCredits = totals.total_credits_earned || 0;
                history.value = dashboard_Data.history || [];
            } catch (e) {
                console.error('Failed to load referral dashboard', e);
            }
        }

        async function loadReferralInfo() {
            isLoadingReferral.value = true;
            referralError.value = '';
            try {
                const data = await getReferralSenderInfo();
                referralInfo.value = data || referralInfo.value;
            } catch (e) {
                referralError.value = 'We could not load your referral link. Please try again later.';
                console.error('Failed to load referral sender info', e);
            } finally {
                isLoadingReferral.value = false;
            }
        }

        onMounted(async () => {
            await Promise.all([
                loadReferralDashboard(),
                loadReferralInfo(),
            ]);
        });
        return {
            stats,
            history,
            referralInfo,
            copyButtonText,
            copyLink,
            referralLink,
            referralLinkPlaceholder,
            referralStatusMessage,
            canCopyReferralLink,
            showQr,
            showQrPopup,
            qrBase64,
            hasQr,
            formatDate,
        };
    },
};
</script>

<style scoped>
.main-content {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

.content-card {
    background-color: var(--background-white);
    padding: 2.5rem;
}

.page-header {
    text-align: left;
    align-items: flex-start;
    display: flex;
    flex-direction: column;
}

.page-header p {
    font-size: 1.1rem;
    color: var(--text-secondary);
}

.referral-banner {
    width: 100%;
    background: linear-gradient(to right, #fffdfa 0%, #ffe6c3 100%);
    border-radius: 12px;
    padding: 2rem 2.5rem;
    margin-bottom: 2rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 2rem;
    box-shadow: 0 2px 8px rgba(255, 230, 195, 0.12);
    min-height: 120px;
}

.banner-left {
    display: flex;
    align-items: center;
    gap: 1.5rem;
}

.banner-icon {
    width: 64px;
    height: 64px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.7);
    border-radius: 50%;
    font-size: 2.5rem;
}

.banner-info {
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
}

.banner-credits {
    color: #b48a00;
    font-weight: 600;
    font-size: 1rem;
}

.banner-title {
    font-size: 1.5rem;
    font-weight: 700;
    color: #222;
}

.banner-right {
    display: flex;
    flex-direction: column;
    align-items: center;
}

.banner-how-title {
    font-size: 1rem;
    font-weight: 700;
    width: 100%;
    color: #444;
}

.banner-how-list {
    list-style: none;
    padding: 0;
    margin: 0;
    font-size: 1rem;
    color: #444;
}

.banner-how-list li {
    margin-bottom: 0.2rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.how-list-icon {
    width: 18px;
    height: 18px;
    margin-right: 0.5rem;
    vertical-align: middle;
    flex-shrink: 0;
}

.banner-picture {
    width: 64px;
    height: 64px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 2.5rem;
}

.referral-link-box {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-top: 2rem;
    background: #F8F9FB;
    border: 1px dashed var(--border-color);
    border-radius: 8px;
    padding: 1.5rem;
}

.referral-link-box input {
    flex: 1;
    background: transparent;
    border: none;
    font-size: 1rem;
}

.referral-link-box button {
    flex: 0 0 auto;
    width: auto;
}

.referral-status {
    margin-top: 0.75rem;
    color: var(--text-secondary);
    font-size: 0.92rem;
}

.btn {
    font-family: 'Poppins', sans-serif;
    text-decoration: none;
    padding: 12px 24px;
    border-radius: 8px;
    transition: all 0.3s ease;
    display: inline-block;
    border: none;
    cursor: pointer;
    text-align: center;
    font-size: 0.95rem;
}

.btn-link {
    background-color: var(--purple);
    color: white;
}

.btn-link:hover {
    background-color: var(--purple-hover);
}

.btn-qr {
    background: var(--background-white-hover-darker);
}

.btn-qr:hover {
    background: var(--background-white-hover-deep-darker);
}

.btn:disabled,
.btn:disabled:hover {
    opacity: 0.5;
    cursor: not-allowed;
}

.stats-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1.5rem;
    margin-top: 2.5rem;
}

.stat-card {
    background-color: #F8F9FB;
    padding: 1.5rem;
    border-radius: 8px;
}

.stat-card .label {
    color: var(--text-secondary);
    margin-bottom: 0.5rem;
}

.stat-card .value {
    font-size: 2rem;
    font-weight: 700;
    color: var(--primary-blue);
}

.history-table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 1rem;
}

.history-table th,
.history-table td {
    text-align: left;
    padding: 1rem;
    border-bottom: 1px solid var(--border-color);
}

.history-table th {
    font-size: 0.85rem;
    text-transform: uppercase;
    color: var(--text-secondary);
    font-weight: 600;
}

.history-table .credits {
    color: var(--success-green);
    font-weight: 500;
}

@media (max-width: 900px) {
    .referral-banner {
        flex-direction: column;
        align-items: stretch;
        gap: 1.5rem;
        padding: 1.5rem 1rem;
    }

    .banner-right {
        flex-direction: column;
        gap: 1rem;
    }
}

@media (max-width: 992px) {
    .main-content-grid {
        grid-template-columns: 1fr;
    }
}

@media (max-width: 768px) {
    .page-wrapper {
        padding: 1rem;
    }

    .top-header {
        flex-direction: column;
        gap: 1rem;
    }

    .referral-link-box {
        flex-direction: column;
        gap: 1rem;
        align-items: stretch;
    }

    .btn {
        width: 100%;
    }
}
</style>
